/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { I18NextPipe } from 'angular-i18next';
import { isEqual } from 'lodash';
import { TableCellDirective, TableComponent, TableHeader } from 'mbs-ui-kit';
import { PagedResponse } from 'mbs-ui-shared@models';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-paged-list-select',
  templateUrl: './paged-list-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbTooltipModule, TableCellDirective, TableComponent]
})
export class PagedListSelectComponent<T> implements ControlValueAccessor, OnInit, AfterViewInit {
  controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
  protected ngControl = inject(NgControl, { optional: true, self: true });
  protected i18nPipe = inject(I18NextPipe);

  public excludeItem = input<{ fieldName: string; value: string }>(null);
  public multipleSelect = input<boolean>(true);
  public showSelectedCounter = input<boolean>(false);
  public selectAllInitially = model<boolean>(false);
  public headerName = model<string>('');
  public value = model<T[]>();
  public disabled = model<boolean>(false);
  public formControlName = input<string>(null);
  public maxHeight = input<string>('50vh');
  public selectionChange = output<T[]>();

  protected control: FormControl<T[]>;
  protected bindLabel = 'name';
  protected bindValue = 'id';
  protected loading = signal(false);
  protected items = signal<T[]>([]);
  protected totalSelectedItems = signal<number>(0);
  protected totalLoadedItems = signal<number>(0);
  protected totalItems = signal<number>(0);
  protected totalVisibleItems = computed(() => Math.max(this.items().length, this.totalItems()));
  protected allItemsLoaded = computed(() => this.totalItems() > 0 && this.totalLoadedItems() >= this.totalItems());
  public readonly selectedAll = computed(() => this.totalSelectedItems() === this.totalItems());
  protected selected$ = new BehaviorSubject<T[]>([]);
  protected fetchData$ = new Subject<{ loadMore?: boolean; reloadData?: boolean }>(); // value - true: fetch more data, false: initial load
  protected headers: TableHeader[] = [
    {
      name: '',
      overflow: true,
      class: 'pl-0',
      gridColSize: '10fr'
    }
  ];
  protected table = viewChild<TableComponent>(TableComponent);

  private selectAllInitiallyCompleted = false;
  private proposedValues: T[];

  constructor() {
    const ngControl = this.ngControl;

    this.ngControl = ngControl || ({} as any);

    if (ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      const myValue = this.value();
      this.onChange?.(myValue);
      this.selectionChange.emit(myValue);
    });
    effect(() => {
      const header = this.headerName();

      this.headers[0].name =
        this.showSelectedCounter() && this.totalSelectedItems()
          ? `${header} (${this.i18nPipe.transform('shared:common.selected')}: ${this.totalSelectedItems()}/${this.totalItems()})`
          : header;

      if (!this.multipleSelect()) this.headers[0].class = 'w-100 cursor-pointer';

      this.headers = [...this.headers];
    });
  }

  ngOnInit() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName()) as FormControl;
      } else {
        console.warn('Missing formControlName directive');
      }
    } else {
      this.control = this.ngControl.control as FormControl;
    }
  }

  ngAfterViewInit(): void {
    this.initStreams();
  }

  writeValue(obj: T[]): void {
    if (isEqual(this.proposedValues, obj) || (!(this.proposedValues as T[])?.length && !(obj as T[])?.length)) return;

    this.proposedValues = obj;
    this.value.set(this.proposedValues);
    this.selected$.next(obj || []);
    this.totalSelectedItems.set((obj || []).length);
  }

  protected getData$(loadMore = false): Observable<PagedResponse<T>> {
    return of({ data: [], total: 0 });
  }

  protected get loadingData$(): Observable<boolean> {
    return of(false);
  }

  protected getByIds$(ids: string[]): Observable<T[]> {
    return of(null);
  }

  protected get dataHasError$(): Observable<boolean> {
    return of(false);
  }

  protected get loadingByIds$(): Observable<boolean> {
    return of(false);
  }

  protected areItemsTheSame(selectedItem: T, item: T): boolean {
    return false;
  }

  private initStreams() {
    combineLatest([this.loadingData$, this.loadingByIds$])
      .pipe(
        map(([loadingData, loadingByIds]) => loadingData || loadingByIds),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((loading) => this.loading.set(loading));

    this.table()
      .virtualScrollViewChange.pipe(
        filter((event) => event.end + 10 >= this.items().length),
        untilDestroyed(this)
      )
      .subscribe(() => this.fetchData$.next({ loadMore: true, reloadData: false }));

    this.selected$
      .pipe(
        map((selected) => selected.map((item) => item[this.bindValue])),
        switchMap((selectedIds) =>
          selectedIds?.length
            ? this.getByIds$(selectedIds).pipe(
                filter((items) => !!items.filter(Boolean).length),
                take(1)
              )
            : of([])
        )
      )
      .pipe(untilDestroyed(this))
      .subscribe((selectedItems) => {
        this.table().selectedItems = selectedItems;
        this.changeSelected(selectedItems);
        this.totalSelectedItems.set(selectedItems.length);
        this.setData(selectedItems, this.items());
      });

    this.fetchData$
      .pipe(
        withLatestFrom(this.dataHasError$),
        filter(
          ([{ loadMore, reloadData }, hasError]) => ((loadMore && !this.allItemsLoaded()) || reloadData || !this.totalItems()) && !hasError
        ),
        switchMap(([params]) =>
          this.loadingData$.pipe(
            filter((loading) => !loading),
            take(1),
            map(() => params)
          )
        ),
        switchMap((params) => this.getData$(params.loadMore).pipe(map((result) => ({ result, ...params })))),
        untilDestroyed(this)
      )
      .subscribe(({ result, loadMore, reloadData }) => {
        this.totalItems.set(result.total);
        this.totalLoadedItems.set(result.data?.length || 0);
        if (reloadData) {
          this.table().selectedItems = [];
          this.changeSelected([]);
          this.totalSelectedItems.set(0);
        }
        if (this.selectAllInitially() && !this.selectAllInitiallyCompleted && result.total > 0 && (result.data as T[]).length > 0) {
          const data = result.data as T[];

          this.selectAllInitiallyCompleted = true;
          this.table().selectedItems = data;
          this.changeSelected(data);
          this.selectAll(result.total);
        }
        this.setData((this.value() as T[]) || [], (result.data as T[]) || []);
      });

    this.fetchData$.next({});
  }

  private setData(selected: T[], items: T[]): void {
    let newData = items.filter((item) => !selected.some((selectedItem) => this.areItemsTheSame(selectedItem, item)));
    const excludeItem = this.excludeItem();

    if (excludeItem) newData = newData.filter((el) => el[excludeItem.fieldName] !== excludeItem.value);

    this.items.set(selected.concat(newData));
  }

  protected changeSelected(event: T[]): void {
    this.value.set(event);
  }

  protected trackBy(item) {
    return item?.id;
  }

  protected selectAll(itemsCount: number) {
    this.totalSelectedItems.set(itemsCount);
    // this.totalSelectedItems.set(itemsCount === this.totalLoadedItems() ? this.totalItems() : itemsCount);
  }

  protected selectedCountText(): string {
    const selected = this.i18nPipe.transform('shared:common.selected', { format: 'title' });
    const all = this.selectedAll() ? 'all' : '';
    const items = this.totalSelectedItems() ? 'computers' : 'computer';
    return `${selected} ${all} ${this.totalSelectedItems()} ${items}.`;
  }

  onChange: (value: T[]) => void;
  onTouched: () => void;

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
